import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import { ApiError } from '../api/client.js';
import { BrandMark } from '../components/icons.js';

type Mode = 'login' | 'register';

export function LoginPage(): JSX.Element {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password, displayName);
      }
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Verbindung fehlgeschlagen. Ist der Server erreichbar?',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center-screen">
      <div className="auth-card">
        <div className="brand">
          <BrandMark className="brand-mark" />
          <span className="brand-name">NutriGeist</span>
        </div>

        <div className="card">
          <div className="segmented" style={{ marginBottom: 16 }}>
            <button
              type="button"
              className={mode === 'login' ? 'active' : ''}
              onClick={() => {
                setMode('login');
                setError(null);
              }}
            >
              Anmelden
            </button>
            <button
              type="button"
              className={mode === 'register' ? 'active' : ''}
              onClick={() => {
                setMode('register');
                setError(null);
              }}
            >
              Registrieren
            </button>
          </div>

          <form className="stack" onSubmit={(event) => void handleSubmit(event)}>
            {mode === 'register' && (
              <div className="field">
                <label htmlFor="displayName">Name</label>
                <input
                  id="displayName"
                  className="input"
                  autoComplete="name"
                  required
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              </div>
            )}

            <div className="field">
              <label htmlFor="email">E-Mail</label>
              <input
                id="email"
                className="input"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="password">Passwort</label>
              <input
                id="password"
                className="input"
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
                minLength={mode === 'register' ? 12 : undefined}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              {mode === 'register' && <span className="hint">Mindestens 12 Zeichen.</span>}
            </div>

            {error && (
              <div className="banner banner-error" role="alert">
                {error}
              </div>
            )}

            <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
              {busy ? (
                <span className="spinner" />
              ) : mode === 'login' ? (
                'Anmelden'
              ) : (
                'Konto anlegen'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
